import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../api/client';
import { authAPI } from '../api/auth';
import { prefetchGlobalTable } from '../hooks/prefetchGlobalTable';
import {
  ClassicLoginCard,
  FeatureBar,
  HeroSection,
  TopBar,
  WalletLoginCard,
} from '../components/login/LoginSections';
import { AVAILABLE_LANGUAGES, COLORS, LOGIN_LINE_IDS, PROD_LOGIN_SHOW_WALLET, REMEMBER_USERNAME_KEY, TOPBAR_OFFSET } from '../constants';
import { useAuthStore } from '../store/auth';
import { useWalletAuthLogin } from '../wallet/useWalletAuthLogin';

export function Login() {
  const { t, i18n } = useTranslation();
  const [showLineDropdown, setShowLineDropdown] = useState(false);
  const [selectedLine, setSelectedLine] = useState('');
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const setTokens = useAuthStore((s) => s.setTokens);
  const setProfile = useAuthStore((s) => s.setProfile);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const wallet = useWalletAuthLogin();

  const isZh = i18n.language === 'zh' || i18n.language?.startsWith('zh');
  const lines = LOGIN_LINE_IDS.map((id) => ({ id, label: t(`auth.${id}`) }));
  const currentLine = lines.find((line) => line.id === selectedLine);
  const currentLanguage = AVAILABLE_LANGUAGES.find((lang) =>
    i18n.language === lang.code || i18n.language?.startsWith(lang.code)
  ) || AVAILABLE_LANGUAGES[0];

  useEffect(() => {
    const saved = localStorage.getItem(REMEMBER_USERNAME_KEY);
    if (saved) {
      setUsername(saved);
      setRememberMe(true);
    }
  }, []);

  function handleLanguage(lang: string) {
    i18n.changeLanguage(lang);
    localStorage.setItem('uo_lang', lang);
  }

  async function completeSession(tokens: { access_token: string; refresh_token: string }) {
    setTokens(tokens.access_token, tokens.refresh_token);
    const profile = await authAPI.me();
    setProfile(profile as never);
    prefetchGlobalTable(qc);
    navigate('/wallet', { replace: true });
  }

  async function handleConnectWallet() {
    if (wallet.isConnected && wallet.address) {
      try {
        const tokens = await wallet.loginWithConnectedWallet(wallet.address);
        if (!tokens?.access_token) return;
        await completeSession(tokens);
      } catch (error) {
        if (!(error instanceof ApiError) && !(error instanceof Error)) {
          wallet.resetWalletAttempt();
        }
      }
      return;
    }
    await wallet.openWalletModal();
  }

  useEffect(() => {
    if (!wallet.isConnected || !wallet.address || wallet.busy) return;
    void (async () => {
      try {
        const tokens = await wallet.loginWithConnectedWallet(wallet.address!);
        if (!tokens?.access_token) return;
        await completeSession(tokens);
      } catch {
        // Keep wallet.error (e.g. 未绑定) — do not clear on auto-login failure.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per connection
  }, [wallet.isConnected, wallet.address]);

  async function handleClassicLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!agreedTerms) {
      setLoginError(t('auth.agreeTerms'));
      return;
    }
    setLoginError(null);
    setLoginLoading(true);

    try {
      const tokens = await authAPI.login({ username: username.trim(), password });
      if (rememberMe) {
        localStorage.setItem(REMEMBER_USERNAME_KEY, username.trim());
      } else {
        localStorage.removeItem(REMEMBER_USERNAME_KEY);
      }
      await completeSession(tokens);
    } catch (error) {
      setLoginError(error instanceof ApiError ? error.message : t('auth.loginFailed'));
    } finally {
      setLoginLoading(false);
    }
  }

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: COLORS.bg }}>
      <div className="relative mx-auto min-h-screen w-full max-w-[430px]">
        <TopBar
          t={t}
          isZh={isZh}
          lines={lines}
          currentLine={currentLine}
          selectedLine={selectedLine}
          showLineDropdown={showLineDropdown}
          setSelectedLine={setSelectedLine}
          setShowLineDropdown={setShowLineDropdown}
          currentLanguage={currentLanguage}
          showLangDropdown={showLangDropdown}
          setShowLangDropdown={setShowLangDropdown}
          onLanguageChange={handleLanguage}
        />

        <main className="relative" style={{ paddingTop: TOPBAR_OFFSET }}>
          <HeroSection t={t} />
          <section className="relative z-20 -top-2 space-y-3 px-6 pb-6">
            <ClassicLoginCard
              t={t}
              username={username}
              password={password}
              rememberMe={rememberMe}
              agreedTerms={agreedTerms}
              showPassword={showPassword}
              loading={loginLoading}
              error={loginError}
              onUsernameChange={setUsername}
              onPasswordChange={setPassword}
              onRememberMeChange={setRememberMe}
              onAgreedTermsChange={setAgreedTerms}
              onShowPasswordToggle={() => setShowPassword((v) => !v)}
              onSubmit={handleClassicLogin}
            />

            {PROD_LOGIN_SHOW_WALLET && (
              <>
                <DividerLabel label={t('auth.otherLogin')} />
                <WalletLoginCard
                  t={t}
                  busy={wallet.busy}
                  error={wallet.error ? (wallet.error.startsWith('auth.') ? t(wallet.error) : wallet.error) : null}
                  onConnectWallet={() => void handleConnectWallet()}
                  onWalletSelect={() => void handleConnectWallet()}
                />
              </>
            )}

            <FeatureBar t={t} />
          </section>
        </main>
      </div>
    </div>
  );
}

function DividerLabel({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 py-1">
      <div className="h-px flex-1" style={{ background: COLORS.line }} />
      <span className="text-xs" style={{ color: COLORS.iconColor }}>{label}</span>
      <div className="h-px flex-1" style={{ background: COLORS.line }} />
    </div>
  );
}
